// ProjectApiHubDocs.js
// Embed Swagger UI cho APIHub OpenAPI spec auto-gen.
// Strategy: load Swagger UI v5 từ CDN (https://unpkg.com) — không cần `npm install swagger-ui-react`
// để tránh build heavy / chậm. Component fallback an toàn khi CDN block:
//   - Hiển thị URL raw spec + nút "Mở editor.swagger.io"
//   - Cho phép paste API key (lưu localStorage) để Swagger UI auto-auth qua requestInterceptor.
//
// Spec URL: /api/v1/projects/{projectName}/apihub/openapi.json
// JWT: gửi từ localStorage 'serviceToken' (đồng bộ axios interceptor toàn dự án).

import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, Button, TextField, Typography, CircularProgress, Stack } from '@mui/material';
import * as TablerIcons from '@tabler/icons-react';

const IconRefresh = TablerIcons.IconRefresh || (() => null);
const IconExternalLink = TablerIcons.IconExternalLink || (() => null);
const IconKey = TablerIcons.IconKey || (() => null);

// CDN URLs (Swagger UI 5.x: stable, hỗ trợ OpenAPI 3.1; ta phục vụ 3.0.3 vẫn ok)
const SWAGGER_CSS_URL = 'https://unpkg.com/swagger-ui-dist@5.17.14/swagger-ui.css';
const SWAGGER_JS_URL = 'https://unpkg.com/swagger-ui-dist@5.17.14/swagger-ui-bundle.js';

let _loadingPromise = null;

function loadSwaggerUiBundle() {
    if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
    if (window.SwaggerUIBundle) return Promise.resolve(window.SwaggerUIBundle);
    if (_loadingPromise) return _loadingPromise;
    _loadingPromise = new Promise((resolve, reject) => {
        // CSS
        if (!document.querySelector(`link[data-swagger-ui-css="1"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = SWAGGER_CSS_URL;
            link.setAttribute('data-swagger-ui-css', '1');
            document.head.appendChild(link);
        }
        // JS
        const existing = document.querySelector(`script[data-swagger-ui-js="1"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(window.SwaggerUIBundle));
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.src = SWAGGER_JS_URL;
        s.async = true;
        s.setAttribute('data-swagger-ui-js', '1');
        s.onload = () => {
            if (window.SwaggerUIBundle) resolve(window.SwaggerUIBundle);
            else reject(new Error('SwaggerUIBundle missing after script load'));
        };
        s.onerror = () => reject(new Error('Failed to load Swagger UI bundle from CDN'));
        document.head.appendChild(s);
    });
    return _loadingPromise;
}

const LS_KEY_API_KEY = 'apihub_swagger_api_key';

export default function ProjectApiHubDocs({ projectName }) {
    const containerRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiKey, setApiKey] = useState(() => {
        try {
            return localStorage.getItem(LS_KEY_API_KEY) || '';
        } catch (e) {
            return '';
        }
    });
    const [reloadTick, setReloadTick] = useState(0);

    const specUrl = `/api/v1/projects/${encodeURIComponent(projectName || '')}/apihub/openapi.json`;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        if (!projectName) {
            setLoading(false);
            return () => {};
        }
        loadSwaggerUiBundle()
            .then((SwaggerUIBundle) => {
                if (cancelled) return;
                if (!containerRef.current) return;
                // Clear previous render (re-init)
                containerRef.current.innerHTML = '';
                let jwt = '';
                try {
                    jwt = localStorage.getItem('serviceToken') || '';
                } catch (e) {
                    jwt = '';
                }
                try {
                    SwaggerUIBundle({
                        url: specUrl,
                        domNode: containerRef.current,
                        deepLinking: false,
                        presets: [SwaggerUIBundle.presets.apis],
                        layout: 'BaseLayout',
                        docExpansion: 'list',
                        defaultModelsExpandDepth: -1,
                        requestInterceptor: (req) => {
                            // Inject API key cho mọi request "Try it out"
                            if (apiKey) req.headers['X-API-Key'] = apiKey;
                            // JWT cho yêu cầu fetch spec (cùng-origin /api/v1)
                            if (jwt && req.url && req.url.includes('/api/v1/')) {
                                req.headers['Authorization'] = `Bearer ${jwt}`;
                            }
                            return req;
                        },
                        onComplete: () => {
                            if (!cancelled) setLoading(false);
                        },
                    });
                } catch (e) {
                    if (!cancelled) setError(e?.message || 'Không init được Swagger UI');
                    if (!cancelled) setLoading(false);
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Không load được Swagger UI từ CDN');
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectName, specUrl, apiKey, reloadTick]);

    const handleSaveKey = (val) => {
        setApiKey(val);
        try {
            if (val) localStorage.setItem(LS_KEY_API_KEY, val);
            else localStorage.removeItem(LS_KEY_API_KEY);
        } catch (e) {
            /* ignore */
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ bgcolor: 'action.hover' }}>
                Swagger UI hiển thị OpenAPI 3.0 spec tự sinh từ <code>endpoints.json</code> + AST parse các service .py.
                Dán API key (header <code>X-API-Key</code>) bên dưới để dùng nút <em>Try it out</em>.
                Spec cache 5 phút trong Redis — sửa routes/services rồi bấm Reload để bust cache.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                    size="small"
                    fullWidth
                    label="X-API-Key (dán key project)"
                    value={apiKey}
                    onChange={(e) => handleSaveKey(e.target.value)}
                    placeholder="Paste API key from tab API Keys..."
                    InputProps={{ startAdornment: <IconKey size={16} style={{ marginRight: 6, opacity: 0.6 }} /> }}
                />
                <Button
                    variant="outlined"
                    startIcon={<IconRefresh size={16} />}
                    onClick={() => setReloadTick((t) => t + 1)}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Reload spec
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<IconExternalLink size={16} />}
                    href={specUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Spec JSON
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<IconExternalLink size={16} />}
                    href={`https://editor.swagger.io/?url=${encodeURIComponent(window.location.origin + specUrl)}`}
                    target="_blank"
                    rel="noopener"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Mở editor.swagger.io
                </Button>
            </Stack>

            {error && (
                <Alert severity="warning">
                    <Typography variant="body2">Swagger UI lỗi: {error}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Có thể CDN unpkg.com bị chặn. Dùng nút &quot;Mở editor.swagger.io&quot; bên trên hoặc &quot;Spec JSON&quot; để xem raw.
                    </Typography>
                </Alert>
            )}

            {loading && !error && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Đang load Swagger UI...</Typography>
                </Box>
            )}

            <Box
                ref={containerRef}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    minHeight: 320,
                    p: 1,
                    // Scope reset cho swagger-ui (ngăn xung đột Tailwind/MUI nếu có)
                    '& .swagger-ui': { fontFamily: 'inherit' },
                }}
            />
        </Box>
    );
}
